import Contract from '@/models/contract';

const SCHEMA_CONTEXT = 'https://schema.org';

function escapeJsonLd(schema) {
  // '<' never appears in JSON syntax outside a string value, so replacing every
  // occurrence neutralizes `</script>`/`<!--` breakout attempts without touching validity.
  return JSON.stringify(schema).replace(/</g, '\\u003c');
}

export function buildJsonLdTag(schema, key) {
  return {
    key,
    type: 'application/ld+json',
    innerHTML: escapeJsonLd(schema),
  };
}

export function buildCollectionPageSchema({ name, description, url, items }) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'CollectionPage',
    name,
    description,
    url,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        url: item.url,
      })),
    },
  };
}

export function buildMarketDetailSchema({ origin, canonicalUrl, market, contracts }) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': canonicalUrl,
        url: canonicalUrl,
        name: market.seoTitle,
        description: market.seoDescription,
        image: market.imageName,
        isPartOf: { '@type': 'WebSite', name: 'PredictIt', url: origin },
        mainEntity: { '@id': '#contracts' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: origin },
          { '@type': 'ListItem', position: 2, name: 'Markets', item: `${origin}/markets` },
          { '@type': 'ListItem', position: 3, name: market.marketName },
        ],
      },
      {
        '@type': 'ItemList',
        '@id': '#contracts',
        name: 'Contracts in this market',
        numberOfItems: contracts.length,
        itemListElement: contracts.map((contract, index) => {
          const contractModel = new Contract(contract);
          const cents = contractModel.lastTradePriceAsCents;
          return {
            '@type': 'ListItem',
            position: index + 1,
            name: contractModel.contractName || contractModel.name,
            description: `Latest trade price: ${cents}¢ per share (market-implied probability ~${cents}%).`,
          };
        }),
      },
    ],
  };
}

export function buildNewsArticleSchema({ origin, canonicalUrl, story, description }) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@type': 'NewsArticle',
    headline: story.seoTitle,
    description: description || story.seoDescription,
    image: story.imageName,
    mainEntityOfPage: canonicalUrl,
    author: { '@type': 'Organization', name: 'PredictIt' },
    publisher: {
      '@type': 'Organization',
      name: 'PredictIt',
      logo: { '@type': 'ImageObject', url: `${origin}/static/logo.png` },
    },
    ...(story.publishedDate ? { datePublished: story.publishedDate } : {}),
    ...(story.modifiedDate ? { dateModified: story.modifiedDate } : {}),
  };
}

export function buildOrganizationWebsiteSchema({ origin, homeName, websiteDescription, sameAs }) {
  return {
    '@context': SCHEMA_CONTEXT,
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${origin}/#organization`,
        name: 'PredictIt',
        url: origin,
        logo: { '@type': 'ImageObject', url: `${origin}/static/logo.png` },
        sameAs: sameAs.filter(Boolean),
      },
      {
        '@type': 'WebSite',
        '@id': `${origin}/#website`,
        url: origin,
        name: 'PredictIt',
        description: websiteDescription,
        publisher: { '@id': `${origin}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: {
            '@type': 'EntryPoint',
            urlTemplate: `${origin}/markets/search?query={search_term_string}`,
          },
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        '@id': `${origin}/#webpage`,
        url: origin,
        name: homeName,
        isPartOf: { '@id': `${origin}/#website` },
        about: { '@id': `${origin}/#organization` },
      },
    ],
  };
}
